import { useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { getMapaMarcadores, type MapaMarcador } from "@/lib/api-mercado";

/*
 * "Seleccionar nodos…" del mapa de precios — mismo patrón que el selector del
 * Portal (/pml-pnd): filtros por tipo/sistema + búsqueda, tabla con palomeo,
 * seleccionar todos los visibles, contador y Aplicar. La selección alimenta la
 * COMPARACIÓN del mapa, así que puede mezclar nodos (PML) y zonas (PND).
 *
 * No necesita endpoint propio: usa los mismos marcadores que ya pinta el mapa
 * (misma queryKey → si el mapa ya cargó un modo, aquí aparece al instante).
 */

export type Modo = "pml" | "pnd";
export type Seleccion = { modo: Modo; clave: string };

type Props = {
  open: boolean;
  mercado: "mda" | "mtr";
  inicial: Seleccion[];
  onClose: () => void;
  onApply: (seleccion: Seleccion[]) => void;
};

type Fila = MapaMarcador & { modo: Modo };

const key = (s: { modo: Modo; clave: string }) => `${s.modo}:${s.clave}`;
const fmt = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Sin acentos ni mayúsculas, para que "juarez" encuentre "Juárez". */
function plano(s: string | null | undefined): string {
  return (s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function SelectorNodosModal({ open, mercado, inicial, onClose, onApply }: Props) {
  const [tipo, setTipo] = useState<Modo | "todos">("todos");
  const [sistema, setSistema] = useState("");
  const [busca, setBusca] = useState("");
  const [seleccion, setSeleccion] = useState<Map<string, Seleccion>>(new Map());

  // Al abrir, arranca con lo que ya está en la comparación.
  useEffect(() => {
    if (open) setSeleccion(new Map(inicial.map((s) => [key(s), s])));
  }, [open, inicial]);

  const nodosQ = useQuery({
    queryKey: ["mapa", "pml", mercado],
    queryFn: () => getMapaMarcadores("pml", mercado),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });
  const zonasQ = useQuery({
    queryKey: ["mapa", "pnd", mercado],
    queryFn: () => getMapaMarcadores("pnd", mercado),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const todas = useMemo<Fila[]>(() => [
    ...(nodosQ.data?.items ?? []).map((i) => ({ ...i, modo: "pml" as Modo })),
    ...(zonasQ.data?.items ?? []).map((i) => ({ ...i, modo: "pnd" as Modo })),
  ], [nodosQ.data, zonasQ.data]);

  const sistemas = useMemo(
    () => Array.from(new Set(todas.map((f) => f.sistema))).sort(),
    [todas],
  );

  const visibles = useMemo(() => {
    const q = plano(busca.trim());
    return todas.filter((f) =>
      (tipo === "todos" || f.modo === tipo)
      && (!sistema || f.sistema === sistema)
      && (!q || plano(f.clave).includes(q) || plano(f.nombre).includes(q)
          || plano(f.zona).includes(q) || plano(f.municipio).includes(q)
          || plano(f.entidad).includes(q)));
  }, [todas, tipo, sistema, busca]);

  // Forma funcional: varios clics seguidos no se pisan entre sí.
  const toggle = (f: Fila) =>
    setSeleccion((prev) => {
      const k = key(f);
      const next = new Map(prev);
      if (next.has(k)) next.delete(k);
      else next.set(k, { modo: f.modo, clave: f.clave });
      return next;
    });

  const todosVisibles = visibles.length > 0 && visibles.every((f) => seleccion.has(key(f)));
  const toggleVisibles = () =>
    setSeleccion((prev) => {
      const next = new Map(prev);
      if (todosVisibles) visibles.forEach((f) => next.delete(key(f)));
      else visibles.forEach((f) => next.set(key(f), { modo: f.modo, clave: f.clave }));
      return next;
    });

  if (!open) return null;

  const cargando = nodosQ.isLoading || zonasQ.isLoading;
  const error = (nodosQ.error ?? zonasQ.error) as Error | null;
  const n = seleccion.size;
  const nPml = Array.from(seleccion.values()).filter((s) => s.modo === "pml").length;

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900">Seleccionar nodos</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Los seleccionados entran a la comparación del mapa; se pueden mezclar nodos (PML) y
              zonas de carga (PND).
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
            aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b grid grid-cols-1 sm:grid-cols-[140px_140px_1fr] gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Tipo</span>
            <select value={tipo} onChange={(e) => setTipo(e.target.value as Modo | "todos")}
              className="mt-1 border rounded px-2 py-1.5 w-full text-sm bg-white">
              <option value="todos">Todos</option>
              <option value="pml">PML · Nodos</option>
              <option value="pnd">PND · Zonas</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Sistema</span>
            <select value={sistema} onChange={(e) => setSistema(e.target.value)}
              className="mt-1 border rounded px-2 py-1.5 w-full text-sm bg-white">
              <option value="">Todos</option>
              {sistemas.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Buscar</span>
            <div className="mt-1 relative">
              <Search className="w-4 h-4 absolute left-2 top-2 text-slate-400" />
              <input type="text" value={busca} onChange={(e) => setBusca(e.target.value)}
                placeholder="clave, nombre, zona, municipio o estado — ej. 02CIR-115, Juárez, MONTERREY"
                className="border rounded pl-8 pr-2 py-1.5 w-full text-sm" autoFocus />
            </div>
          </label>
        </div>

        <div className="flex-1 overflow-auto">
          {cargando && <div className="px-5 py-8 text-center text-sm text-slate-500">Cargando catálogo…</div>}
          {error && <div className="px-5 py-8 text-center text-sm text-red-600">{error.message}</div>}
          {!cargando && !error && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input type="checkbox" checked={todosVisibles}
                      ref={(el) => { if (el) el.indeterminate = !todosVisibles && visibles.some((f) => seleccion.has(key(f))); }}
                      onChange={toggleVisibles}
                      title={todosVisibles ? "Quitar todos los visibles" : `Seleccionar los ${visibles.length} visibles`}
                      className="w-3.5 h-3.5 accent-blue-600 cursor-pointer align-middle" />
                  </th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Clave</th>
                  <th className="px-3 py-2 font-medium">Nombre</th>
                  <th className="px-3 py-2 font-medium">Zona de carga</th>
                  <th className="px-3 py-2 font-medium">Municipio</th>
                  <th className="px-3 py-2 font-medium">Sistema</th>
                  <th className="px-3 py-2 font-medium text-right whitespace-nowrap"
                    title="Promedio del último día publicado">
                    {mercado.toUpperCase()} $/MWh
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Sin resultados.</td></tr>
                )}
                {visibles.map((f) => {
                  const k = key(f);
                  const on = seleccion.has(k);
                  return (
                    <tr key={k} onClick={() => toggle(f)}
                      className={`border-t cursor-pointer ${on ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                      <td className="px-3 py-1.5">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          on ? "bg-blue-600 border-blue-600" : "bg-white border-slate-300"}`}>
                          {on && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${
                          f.modo === "pml" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"}`}>
                          {f.modo.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs">{f.clave}</td>
                      <td className="px-3 py-1.5 text-slate-700">{f.modo === "pml" ? f.nombre : `${f.n_nodos ?? "—"} nodos`}</td>
                      <td className="px-3 py-1.5 text-slate-600">{f.modo === "pml" ? f.zona ?? "—" : "—"}</td>
                      <td className="px-3 py-1.5 text-slate-600">{f.municipio ?? "—"}</td>
                      <td className="px-3 py-1.5 text-slate-600">{f.sistema}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
                        {f.precio != null ? fmt.format(f.precio) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            <b className="text-slate-800">{n}</b> seleccionado{n === 1 ? "" : "s"}
            {n > 0 && <span className="text-slate-400"> ({nPml} PML · {n - nPml} PND)</span>}
            {!cargando && (
              <span className="text-slate-400"> · {visibles.length} de {todas.length} en la lista</span>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setSeleccion(new Map())}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50">
              Limpiar
            </button>
            <button type="button" onClick={onClose}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50">
              Cancelar
            </button>
            <button type="button"
              onClick={() => { onApply(Array.from(seleccion.values())); onClose(); }}
              className="bg-blue-600 text-white rounded px-4 py-1.5 text-sm hover:bg-blue-700">
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
